import { useMemo } from 'react';
import { useQuery } from '@supabase-cache-helpers/postgrest-react-query';
import { supabaseCoreClient } from '../supabase.ts';
import { commonQueryProps } from './cache.js';
import type { DashboardInfo, DashboardMetadata } from '../../types/dashboard.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

export const buildQueryForDashboardInfo = () =>
  (supabaseCoreClient as any).from('dashboard_info').select('*').order('ui_label');

function normalizeRoute(route: string | null | undefined): string | null {
  if (!route) return null;
  return route.startsWith('/') ? route : `/${route}`;
}

export function dashboardPath(dashboard: Pick<DashboardInfo, 'dashboard_key' | 'route'>): string {
  const route = normalizeRoute(dashboard.route);
  if (!route) return `/dashboard/${dashboard.dashboard_key}`;
  return route;
}

export function postProcessDashboardMetadata(dashboards: DashboardInfo[]): DashboardMetadata {
  const byKey: Record<string, DashboardInfo> = {};
  const byRoute: Record<string, DashboardInfo> = {};

  dashboards.forEach((dashboard) => {
    byKey[dashboard.dashboard_key] = dashboard;

    const route = dashboardPath(dashboard);
    if (route) {
      byRoute[route] = dashboard;
    }

    byRoute[`/dashboard/${dashboard.dashboard_key}`] = dashboard;
  });

  return {
    asList: dashboards,
    byKey,
    byRoute,
    navItems: dashboards.filter((dashboard) => dashboard.show_in_nav && !dashboard.is_draft),
  };
}

interface UseDashboardMetadataOptions {
  enabled?: boolean;
}

interface UseDashboardMetadataResult {
  data: DashboardMetadata | null;
  error: any;
  isLoading: boolean;
}

export function useDashboardMetadata({ enabled }: UseDashboardMetadataOptions): UseDashboardMetadataResult {
  const bootReady = useMetadataBootReady();
  const { data, error, isPending } = useQuery(
    buildQueryForDashboardInfo(),
    { ...commonQueryProps, enabled: enabled && bootReady, meta: persistedRelations('core_crm.dashboard_info') }
  );

  // Memoized on the query result so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(
    () => (data ? postProcessDashboardMetadata(data as unknown as DashboardInfo[]) : null),
    [data]
  );

  if (isPending || error || !data) {
    return { data: null, error: error ?? null, isLoading: isPending };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false,
  };
}
